import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import BuyButton from '../Products/ListBuyButton';
import { translate } from '../../Services/translation';
import useToggleExpand from '../../Hooks/UseToggleExpand';
import VariantListItem from './VariantList/VariantListItem';
import VariantNotification from './VariantNotification';
import constants from '../../constants';

const VariantList = ({
  logos,
  productName,
  variants,
  type,
  comment,
  isOrderItem,
  includeVat,
  displayPriceOnVariants,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [quantities, setQuantities] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const { expanded, expand, recalculateHeight } = useToggleExpand(false);
  const [quantity, setQuantity] = useState(1);
  const isViewOnlyUser = constants.isViewOnlyUser;

  const [showAddWishlist, setShowAddWishlist] = useState(null);

  const handleQuantityChange = (variantId, value) => {
    setQuantities((prevQuantities) => {
      const newList = [...prevQuantities];
      const index = newList.findIndex((x) => x.articleNumber === variantId);
      if (index > -1) {
        if (value <= 0) {
          newList.splice(index, 1);
        } else {
          newList[index].quantity = value;
        }
      } else {
        newList.push({
          quantity: value,
          articleNumber: variantId,
        });
      }

      return newList;
    });
  };

  const toggleShowAll = () => {
    expand(elementRef);

    let element = document.getElementById('product-variant__show-more-list');

    element.className =
      element.className === 'product-variant__show-more-list'
        ? 'product-variant__show-more-list--expanded'
        : 'product-variant__show-more-list';
  };

  const visibleVariants = showAll ? variants : variants.slice(0, 4);
  const hiddenVariants = variants.slice(4);

  useEffect(() => {
    const calculatedTotalAmount = quantities.reduce(
      (acc, item) => {
        const variant = variants.find(
          (v) => v.variantId === item.articleNumber
        );

        if (variant) {
          const unitPrice = includeVat
            ? variant.price.price.priceWithVat
            : variant.price.price.price;

          const totalPrice = +unitPrice.toFixed(2) * item.quantity;

          acc.totalAmount = +acc.totalAmount + totalPrice;

          if (acc.totalAmount > 0) {
            acc.totalAmount = acc.totalAmount.toFixed(2);
          }
        }

        return acc;
      },
      { totalAmount: 0 }
    );

    const calculatedTotalQuantity = quantities.reduce(
      (acc, item) => acc + Number(item.quantity),
      0
    );

    setTotalAmount(calculatedTotalAmount.totalAmount);
    setTotalQuantity(calculatedTotalQuantity);
  }, [quantities, variants]);

  const elementRef = useRef(null);

  useLayoutEffect(() => {
    function updateSize() {
      if (!showAll) {
        return;
      }

      recalculateHeight(elementRef);
    }

    window.addEventListener('resize', updateSize);
    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  if (type === 'configurable') {
    return (
      <>
        {isViewOnlyUser ? (
          <div className="product-variant__bottom-container product-variant__bottom-container--is-fullwidth">
            <button className="product-variant__styled-button product-variant__buy-button">
              {translate('product-variant.viewonlyuser.message')}
            </button>
            <span className="product-variant__shipping-container">
              <img
                className="product-variant__shipping-icon"
                src="/ui/images/fast-delivery-black.svg"
                alt=""
              />
              <p className="product-variant__shipping-text">
                {translate('product.shipping.time')}
              </p>
            </span>
          </div>
        ) : (
          <>
            <VariantNotification />
            <div className="product-variant__button-container product-variant__button-container--small">
              <BuyButton
                comment={comment}
                label={translate('products.add.to.cart', totalQuantity)}
                productName={productName}
                items={[
                  {
                    articleNumber: variants[0].variantId,
                    quantity: quantity,
                  },
                ]}
                type={type}
                logos={logos}
                cssClass="product-variant__styled-button product-variant__styled-button--configurable product-variant__buy-button"
              ></BuyButton>
              <span className="product-variant__shipping-container">
                <img
                  className="product-variant__shipping-icon"
                  src="/ui/images/fast-delivery-black.svg"
                  alt=""
                />
                <p className="product-variant__shipping-text">
                  {translate('product.shipping.time')}
                </p>
              </span>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <div>
      <div className="product-variant__container">
        <div className="product-header__item">
          {translate('product.variant.header')}
        </div>
        <div className="product-header__item">
          {translate('product.instock.header')}
        </div>
        <div className="product-header__item">
          {translate('product.quantity.header')}
        </div>

        {visibleVariants.map((variant, index) => (
          <VariantListItem
            key={index}
            variant={variant}
            handleQuantityChange={handleQuantityChange}
            quantities={quantities}
            setQuantities={setQuantities}
            showAddWishlist={showAddWishlist}
            setShowAddWishlist={setShowAddWishlist}
            isOrderItem={isOrderItem}
            displayPriceOnVariants={displayPriceOnVariants}
          />
        ))}
      </div>
      {hiddenVariants.length > 0 && (
        <div
          className={'product-variant__show-more-list'}
          id="product-variant__show-more-list"
          ref={elementRef}
        >
          <div className="product-variant__container">
            {hiddenVariants.map((variant, index) => (
              <VariantListItem
                key={index}
                variant={variant}
                handleQuantityChange={handleQuantityChange}
                quantities={quantities}
                setQuantities={setQuantities}
                showAddWishlist={showAddWishlist}
                setShowAddWishlist={setShowAddWishlist}
                isOrderItem={isOrderItem}
                displayPriceOnVariants={displayPriceOnVariants}
              />
            ))}
          </div>
        </div>
      )}

      {variants.length > 4 && (
        <button className="product-variant__show-more" onClick={toggleShowAll}>
          <img
            className="product-variant__cross-icon"
            src="/ui/images/plus-dark.svg"
            alt=""
          />
          {expanded
            ? translate('product.show.less')
            : translate('product.show.more')}
        </button>
      )}

      <span
        className="product-variant__total-amount"
        style={{ marginBottom: displayPriceOnVariants && 0 }}
      >
        {translate('product.variant-total')}: {totalAmount}{' '}
        {variants[0].price.currency.symbol}
      </span>

      {displayPriceOnVariants && (
        <p
          className="product-detail__price-vat"
          style={{ marginBottom: displayPriceOnVariants && 25 }}
        >
          {includeVat
            ? translate('product.incl.vat')
            : translate('product.excl.vat')}
        </p>
      )}

      <VariantNotification />

      {isOrderItem && <p>{translate('product.order-item.text')}</p>}

      {isViewOnlyUser ? (
        <div className="product-variant__bottom-container product-variant__bottom-container--is-fullwidth">
          <button className="product-variant__styled-button product-variant__buy-button">
            {translate('product-variant.viewonlyuser.message')}
          </button>
          <span className="product-variant__shipping-container">
            <img
              className="product-variant__shipping-icon"
              src="/ui/images/fast-delivery-black.svg"
              alt=""
            />
            <p className="product-variant__shipping-text">
              {translate('product.shipping.time')}
            </p>
          </span>
        </div>
      ) : (
        <>
          <BuyButton
            label={translate('products.add.to.cart', totalQuantity)}
            disabled
            productName={productName}
            items={quantities}
            logos={logos}
            type={type}
            comment={comment}
            cssClass="product-variant__styled-button product-variant__buy-button"
          ></BuyButton>

          <span className="product-variant__shipping-container">
            <img
              className="product-variant__shipping-icon"
              src="/ui/images/fast-delivery-black.svg"
              alt=""
            />
            <p className="product-variant__shipping-text">
              {translate('product.shipping.time')}
            </p>
          </span>
        </>
      )}
      {/*<StickyCart*/}
      {/*    productName={productName}*/}
      {/*    totalQuantity={totalQuantity}*/}
      {/*    quantities={quantities}*/}
      {/*    model={stickyCartModel}*/}
      {/*    show={quantities.length > 0}/>*/}
    </div>
  );
};

export default VariantList;
